import { useEffect, useState } from "react";
import DataTable from "../DataTable";
import PageContent from "../PageContent";
import TableContainer from "../TableContainer";
import { formatQuantidade } from "../../../service/utils/formatQuantidade";

function ReverterEntregaModal({ show, onClose, onConfirm, entityType, entityId, itens, isSaving }) {
  const [tipo, setTipo] = useState('TOTAL');
  const [quantidades, setQuantidades] = useState({});
  const [observacao, setObservacao] = useState('');

  useEffect(() => {
    if (show) {
      setTipo('TOTAL');
      setObservacao('');
      const iniciais = {};
      (itens || []).forEach(item => { iniciais[item.idItem] = ''; });
      setQuantidades(iniciais);
    }
  }, [show, itens]);

  if (!show) return null;

  const handleQtdChange = (idItem, valor) => {
    const item = (itens || []).find(i => i.idItem === idItem);
    const max = item ? (item.qtdeEntregue || 0) : 0;
    const lim = Math.min(Math.max(0, parseFloat(valor) || 0), max);
    setQuantidades(prev => ({ ...prev, [idItem]: lim }));
  };

  const handleConfirm = () => {
    if (tipo === 'TOTAL') {
      onConfirm({ tipo: 'TOTAL', observacao: observacao || null });
      return;
    }
    const itensReverter = (itens || [])
      .filter(item => (quantidades[item.idItem] || 0) > 0)
      .map(item => ({ idItem: item.idItem, qtdeReverter: quantidades[item.idItem] }));
    onConfirm({ tipo: 'PARCIAL', itens: itensReverter, observacao: observacao || null });
  };

  const temQuantidade = tipo === 'TOTAL' || (itens || []).some(item => (quantidades[item.idItem] || 0) > 0);

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg" style={{ maxWidth: '95vw', height: '90vh' }}>
        <div className="modal-content h-100 overflow-hidden">
          <div
            className="modal-header text-white"
            style={{
              background: 'linear-gradient(45deg, rgb(66, 6, 6) 0%, rgba(220, 53, 69, 1) 100%)',
              padding: '0.5rem 1rem',
              borderTopLeftRadius: '0.5rem',
              borderTopRightRadius: '0.5rem',
              border: 'none',
            }}
          >
            <h5 className="modal-title">
              <i className="bi bi-arrow-counterclockwise me-2" />
              Reverter Entrega — {entityType} #{entityId}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} disabled={isSaving} />
          </div>
          <PageContent>
            <div className="alert alert-danger py-2 px-3 small d-flex align-items-center">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <span>
                <strong>Atenção:</strong> esta ação irá desfazer a entrega registrada e <strong>excluir as notas fiscais</strong> vinculadas
                {tipo === 'TOTAL' ? ' (reversão total)' : ''}. O status voltará para <strong>PENDENTE</strong> quando todo o saldo for revertido.
              </span>
            </div>

            <div className="d-flex flex-wrap gap-4 mb-2">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="tipoReversao"
                  id="revTotal"
                  checked={tipo === 'TOTAL'}
                  onChange={() => setTipo('TOTAL')}
                  disabled={isSaving}
                />
                <label className="form-check-label fw-semibold" htmlFor="revTotal">
                  Reversão total (zerar todas as quantidades)
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="tipoReversao"
                  id="revParcial"
                  checked={tipo === 'PARCIAL'}
                  onChange={() => setTipo('PARCIAL')}
                  disabled={isSaving}
                />
                <label className="form-check-label fw-semibold" htmlFor="revParcial">
                  Reversão parcial (informar quantidades)
                </label>
              </div>
            </div>

            <TableContainer>
              <DataTable
                style={{ fontSize: '0.8rem' }}
                head={
                  <tr>
                    <th className="text-center align-middle">Descrição</th>
                    <th className="text-center align-middle">Qtde Pedida</th>
                    <th className="text-center align-middle">Já Entregue</th>
                    {tipo === 'PARCIAL' && (
                      <th className="text-center align-middle" style={{ width: 130 }}>Qtde a Reverter</th>
                    )}
                    <th className="text-center align-middle">Saldo Após Reversão</th>
                  </tr>
                }
              >
                <tbody>
                  {(itens || []).map(item => {
                    const qtdRev = tipo === 'TOTAL' ? (item.qtdeEntregue || 0) : (quantidades[item.idItem] || 0);
                    const saldo = (item.qtdeEntregue || 0) - qtdRev;
                    return (
                      <tr key={item.idItem}>
                        <td>{item.descricao} {item.unidade ? `(${item.unidade})` : ''}</td>
                        <td className="text-center">{formatQuantidade(item.qtdePedida)}</td>
                        <td className="text-center">{formatQuantidade(item.qtdeEntregue)}</td>
                        {tipo === 'PARCIAL' && (
                          <td className="text-center">
                            <input
                              type="number"
                              className="form-control form-control-sm text-center"
                              min={0}
                              step="0.01"
                              max={item.qtdeEntregue}
                              value={quantidades[item.idItem] ?? ''}
                              disabled={isSaving || !(item.qtdeEntregue > 0)}
                              onChange={e => handleQtdChange(item.idItem, e.target.value)}
                            />
                          </td>
                        )}
                        <td className={`text-center fw-bold ${saldo === 0 ? 'text-success' : 'text-primary'}`}>
                          {formatQuantidade(saldo)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </DataTable>
            </TableContainer>

            <div className="mt-2">
              <label className="form-label small fw-semibold">Observação (opcional)</label>
              <textarea
                className="form-control form-control-sm"
                rows={2}
                maxLength={255}
                value={observacao}
                onChange={e => setObservacao(e.target.value)}
                placeholder="Motivo da reversão..."
                disabled={isSaving}
              />
            </div>
          </PageContent>
          <div className="modal-footer p-1 d-flex align-items-center gap-1">
            <div className="d-flex justify-content-end align-items-center gap-1 ms-auto">
              <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={isSaving}>
                <i className="bi bi-x-lg me-1" />
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleConfirm}
                disabled={!temQuantidade || isSaving}
              >
                {isSaving ? (
                  <><span className="spinner-border spinner-border-sm me-1"></span>Revertendo...</>
                ) : (
                  <><i className="bi bi-arrow-counterclockwise me-1" />Confirmar Reversão</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReverterEntregaModal;
